import TileMinedLogic from "../../../logic/TileMinedLogic";
import Rand from "../../../util/Rand";
import Character from "../../character/Character";
import WorldGrid from "../../grid/WorldGrid";
import Vector from "../../math/Vector";
import { SpriteDirection } from "../../sprite/SpriteDirection";
import Missile from "./Missile";
import NumberValue from "../../value/NumberValue";
import ValueCalculator from "../../../util/ValueCalculator";
import Explosion from "./Explosion";
import VectorField from "../../grid/VectorField";
import Values from "../../value/Values";
import SkillUtil from "../SkillUtil";

const TWO_PI = Math.PI * 2;

export default class Missiles {
    /**
     * @param {WorldGrid} worldGrid
     * @param {VectorField} vectorField
     * @param {NumberValue} missilesUpgrades
     * @param {TileMinedLogic} tileMinedLogic
     * @param {Values} values
     */
    constructor(worldGrid, vectorField, missilesUpgrades, tileMinedLogic, values) {
        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = vectorField;
        /**
         * @constant
         * @type {Values}
         */
        this.values = values;

        /**
         * @constant
         * @type {NumberValue}
         */
        this.missilesUpgrades = missilesUpgrades;
        /**
         * @constant
         * @type {TileMinedLogic}
         */
        this.tileMinedLogic = tileMinedLogic;
        
        /**
         * @constant
         * @type {Explosion[]}
         */
        this.missileExplosions = new Array(3);
        for (let i = 0; i < this.missileExplosions.length; i++) {
            this.missileExplosions[i] = new Explosion();
        }

        /**
         * @constant
         * @type {Missile[]}
         */
        this.missiles = [];

        /**
         * @type {number}
         */
        this.activeMissiles = 0;
        /**
         * @type {number}
         */
        this.lastFireTime = 0;
        /**
         * @constant
         * @type {number}
         */
        this.millisBetweenFires = 550;

        /**
         * @type {boolean}
         */
        this.skillActive = false;

        /**
         * @type {boolean}
         */
        this.burstActive = false;
        /**
         * @type {number}
         */
        this.burstCount = 0;
        /**
         * @type {number}
         */
        this.burstTime = 0;
        /**
         * @constant
         * @type {number}
         */
        this.burstDelay = 70; // millis

        /**
         * @type {number}
         */
        this.radians = 0;
        /**
         * @type {number}
         */
        this.spriteDirection = 0;

    }

    onSkillActivation() {
        this.skillActive = true;

        const now = Date.now();

        this.burstActive = false;
        this.burstTime = now - this.burstDelay;
        this.activeMissiles = 0;
        this.lastFireTime = now - this.millisBetweenFires; // <-- we want to start firing immediately.

        for (let i = 0; i < this.missileExplosions.length; i++) {
            this.missileExplosions[i].deactivate();
        }
        
        for (let i = 0; i < this.missiles.length; i++) {
            this.missiles[i].resetMissile();
        }
    }

    onSkillDeactivation() {
        this.skillActive = false;
        
        for (let i = 0; i < this.missileExplosions.length; i++) {
            this.missileExplosions[i].deactivate();
        }

        this.burstActive = false;
        this.activeMissiles = 0;
    }

    /**
     * @param {Character} miner
     * @param {number} frameTimeRatio
     */
    updateForFrame(miner, frameTimeRatio) {
        if (!miner.position.tile) {
            return;
        }

        ///////////////////////////////////////////
        // Update active missiles
        ///////////////////////////////////////////

        const now = Date.now();

        // See if it is time for a new burst
        if (!this.burstActive && this.activeMissiles === 0 && (now - this.lastFireTime > this.millisBetweenFires)) {
            this.burstActive = true;
            this.burstCount = 0;
            this.burstTime = now - this.burstDelay;

            // pick direction
            this.chooseBestFireDirection(miner);
        }

        if (this.burstActive) {
            
            // Check to see if we have enough missiles
            const maxCount = ValueCalculator.getMissileCount(this.missilesUpgrades);
            while (this.missiles.length < maxCount) {
                this.missiles.push(new Missile(this.worldGrid, this.tileMinedLogic, this));
            }

            // See if it is time to fire another missile in the burst.
            if (now - this.burstTime >= this.burstDelay) {
                this.burstTime = now;

                // fire missile
                const missile = this.missiles[this.burstCount];
                this.burstCount++;

                missile.onMissileFire(miner.position.worldCoordinateOrigin, this.radians, this.spriteDirection);
                this.activeMissiles++;
            }

            // See if we're done with the burst
            if (this.burstCount >= maxCount) {
                this.burstActive = false;
            }
        }

        if (this.activeMissiles > 0) {
            const movementPerFrame = this.getMissileMovementPerFrame(frameTimeRatio);

            this.activeMissiles = 0;
            for (let i = 0; i < this.missiles.length; i++) {
                const missile = this.missiles[i];
                if (!missile || !missile.active) {
                    continue;
                }
    
                missile.updateForFrame(movementPerFrame);
                if (missile.active) {
                    this.activeMissiles++;
                }
            }

            // If we are done, then start the timer.
            if (this.activeMissiles === 0) {
                this.lastFireTime = now;
            }
        }
    }

    /**
     * @private
     * @param {number} frameTimeRatio 
     * @returns {number}
     */
    getMissileMovementPerFrame(frameTimeRatio) {
        const defaultMovement = 6 * frameTimeRatio;
        
        if (this.values.fallSpeedBonusActive.isValue()) {
            const fallMultiplier = ValueCalculator.getFallSpeedMultiplier(this.values.fallSpeedUpgrades.getValue());
            return defaultMovement * fallMultiplier;
        }
        else {
            return defaultMovement;
        }
    }

    /**
     * @param {Vector} position
     */
    addExplosion(position) {
        const explosion = this.findAvailableExplosion();
        if (!explosion) {
            console.log('Missiles.addExplosion() failed to find available explosion.');
            return;
        }

        explosion.activate(position);
    }

    /**
     * @private
     * @returns {Explosion}
     */
    findAvailableExplosion() {

        const maxCount = ValueCalculator.getMissileCount(this.missilesUpgrades);
        if (this.missileExplosions.length < maxCount) {
            for (let i = 0; i < this.missileExplosions.length; i++) {
                const explosion = this.missileExplosions[i];
                if (!explosion.active) {
                    return explosion;
                }
            }

            const explosion = new Explosion();
            this.missileExplosions.push(explosion);
            return explosion;
        }
        else {
            // Find the oldest explosion (or just find an inactive one)
            let oldestExplosion = null;
            let oldestTime = 0;

            for (let i = 0; i < this.missileExplosions.length; i++) {
                const explosion = this.missileExplosions[i];
                if (!explosion.active) {
                    return explosion;
                }

                if (!oldestExplosion || explosion.time < oldestTime) {
                    oldestExplosion = explosion;
                    oldestTime = explosion.time;
                }
            }

            return oldestExplosion;
        }
    }

    /**
     * @private
     * @param {Character} miner
     */
    chooseBestFireDirection(miner) {
        const targetTile = this.chooseTargetTile(miner);
        if (targetTile && targetTile !== miner.position.tile) {
            // this.workingPosition.copy(targetTile.origin);
            // this.workingPosition.addXY(Settings.tile.halfSize, Settings.tile.halfSize);

            this.radians = this.calculateAngle(miner.position.worldCoordinateOrigin, targetTile.origin);
            this.spriteDirection = this.calculateSpriteDirection(this.radians);
        }
        else {
            this.chooseRandomDirection();
        }
    }

    /**
     * @private
     * @param {Vector} start 
     * @param {Vector} end 
     * @returns {number} radians
     */
    calculateAngle(start, end) {
        // returns the angle between 2 points in radians
        // p1 = {x: 1, y: 2};
        // p2 = {x: 3, y: 4};
        return this.clamp(Math.atan2(end.y - start.y, end.x - start.x));
    }

    /**
     * @param {number} radians 
     * @returns {number}
     */
    clamp(radians) {
        if (radians < 0) {
            return radians + TWO_PI;
        }
        else if (radians > TWO_PI) {
            return radians - TWO_PI;
        }
        return radians;
    }


    /**
     * @private
     * @param {Character} miner 
     */
    chooseTargetTile(miner) {
        const minerPosition = miner.position.worldCoordinateOrigin;

        const priorityTile = SkillUtil.findClosestTile(minerPosition, this.vectorField.priorityMiningCandidates);
        if (priorityTile) {
            return priorityTile;
        }

        const currencyTile = SkillUtil.findClosestTile(minerPosition, this.vectorField.currencyMiningCandidates);
        if (currencyTile) {
            return currencyTile;
        }

        // Just use the mine target as a destination tile.
        const mineTarget = miner.target.mineTile;
        if (mineTarget) {
            return mineTarget;
        }

        const targetTile = miner.target.targetTile;
        if (targetTile) {
            return targetTile;
        }
        
        return null;
    }

    /**
     * @private
     * @param {number} radians 
     * @returns {number}
     */
    calculateSpriteDirection(radians) {


        const angleDegrees = radians * 180 / Math.PI;

        if (angleDegrees >= 337.5 || angleDegrees < 22.5) {
            return SpriteDirection.EAST;
        }
        else if (angleDegrees >= 22.5 && angleDegrees < 67.5) {
            return SpriteDirection.SOUTH_EAST;
        }
        else if (angleDegrees >= 67.5 && angleDegrees < 112.5) {
            return SpriteDirection.SOUTH;
        }
        else if (angleDegrees >= 112.5 && angleDegrees < 157.5) {
            return SpriteDirection.SOUTH_WEST;
        }
        else if (angleDegrees >= 157.5 && angleDegrees < 202.5) {
            return SpriteDirection.WEST;
        }
        else if (angleDegrees >= 202.5 && angleDegrees < 247.5) {
            return SpriteDirection.NORTH_WEST;
        }
        else if (angleDegrees >= 247.5 && angleDegrees < 292.5) {
            return SpriteDirection.NORTH;
        }
        else if (angleDegrees >= 292.5 && angleDegrees < 337.5) {
            return SpriteDirection.NORTH_EAST;
        }

        // Default to north.
        return SpriteDirection.NORTH;
    }


    /**
     * @private
     */
    chooseRandomDirection() {
        const twoPi = 2 * Math.PI;

        const dir = Rand.randomInt(8);
        switch (dir) {
            case 0:
                this.radians = 0;
                this.spriteDirection = SpriteDirection.EAST;
                break;
            case 1:
                this.radians = twoPi * (1 / 8);
                this.spriteDirection = SpriteDirection.SOUTH_EAST;
                break;
            case 2:
                this.radians = twoPi * (1 / 4);
                this.spriteDirection = SpriteDirection.SOUTH;
                break;
            case 3:
                this.radians = twoPi * (3 / 8);
                this.spriteDirection = SpriteDirection.SOUTH_WEST;
                break;
            case 4:
                this.radians = twoPi * (1 / 2);
                this.spriteDirection = SpriteDirection.WEST;
                break;
            case 5:
                this.radians = twoPi * (5 / 8);
                this.spriteDirection = SpriteDirection.NORTH_WEST;
                break;
            case 6:
                this.radians = twoPi * (3 / 4);
                this.spriteDirection = SpriteDirection.NORTH;
                break;
            case 7:
                this.radians = twoPi * (7 / 8);
                this.spriteDirection = SpriteDirection.NORTH_EAST;
                break;
        }
    }

}